import { useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useBrands, usePublish, usePublishHistory, useRollback } from '../api/hooks';
import { ErrorNote, PageHeader, Spinner } from '../components/Layout';
import { SectionHeader } from '../components/ui/primitives';
import { errorMessage, useToast } from '../components/ui/Toast';
import { getConfig } from '../config';

export function PublishPage() {
  const { user } = useAuth();
  const brands = useBrands();
  const history = usePublishHistory();
  const publish = usePublish();
  const rollback = useRollback();
  const [note, setNote] = useState('');
  const toast = useToast();

  const brandList = brands.data?.brands ?? [];
  const counts = brands.data?.counts ?? {};
  const liveBrands = brandList.filter((b) => b.status === 'live');
  const draftBrands = brandList.filter((b) => b.status === 'draft');
  // Only models under a live brand can reach the app, so count them that way.
  const liveModels = liveBrands.reduce((n, b) => n + (counts[b.brandId]?.liveModels ?? 0), 0);
  const running = history.data?.some((r) => r.status === 'pending') ?? false;

  return (
    <div className="page">
      <PageHeader title="Publish" />

      <section className="card">
        <SectionHeader
          title="Push the current draft to the app"
          description="Renders every live brand, model and variant to static JSON on the CDN. Draft items stay hidden, and nothing reaches the mobile app until you publish."
        />

        <div className="stats">
          <Stat label="Live brands" value={liveBrands.length} />
          <Stat label="Live models" value={liveModels} />
          <Stat label="Draft brands" value={draftBrands.length} />
        </div>

        <label className="field">
          <span className="field-label">Note (optional)</span>
          <input
            className="input"
            placeholder="e.g. Added MG HS Trophy pricing"
            value={note}
            maxLength={500}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>

        {publish.error ? <ErrorNote error={publish.error} /> : null}

        <div className="form-actions">
          <button
            type="button"
            className="btn primary"
            disabled={publish.isPending || running}
            data-loading={publish.isPending || undefined}
            onClick={() =>
              publish.mutate(note, {
                onSuccess: () => {
                  setNote('');
                  toast.success('Publish started — this page will update when it finishes.');
                },
                onError: (err) => toast.error(errorMessage(err)),
              })
            }
          >
            {running ? 'Publish in progress…' : 'Publish now'}
          </button>
        </div>

        <p className="field-hint">
          Live feed:{' '}
          <a href={`${getConfig().contentBaseUrl}/v1/manifest.json`} target="_blank" rel="noreferrer">
            {getConfig().contentBaseUrl}/v1/manifest.json
          </a>
        </p>
      </section>

      <section className="card">
        <SectionHeader title="History" description="The last 25 publishes, newest first." />
        {history.isLoading ? <Spinner label="Loading history…" /> : null}
        {history.error ? <ErrorNote error={history.error} /> : null}

        {history.data?.length === 0 ? (
          <p className="empty-note">Nothing published yet.</p>
        ) : (
          <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>By</th>
                <th>Note</th>
                <th>Contents</th>
                <th>Status</th>
                {user?.isAdmin ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {(history.data ?? []).map((record) => (
                <tr key={record.publishId}>
                  <td title={record.startedAt}>{formatWhen(record.startedAt)}</td>
                  <td>{record.requestedBy}</td>
                  <td>
                    {record.note || <span className="muted">—</span>}
                    {record.rollbackOf ? <span className="badge role">rollback</span> : null}
                  </td>
                  <td className="muted">
                    {record.counts
                      ? `${record.counts.brands} brands · ${record.counts.models} models · ${record.counts.files} files`
                      : '—'}
                  </td>
                  <td>
                    <span className={`badge ${record.status}`}>{record.status}</span>
                    {record.error ? <div className="field-error">{record.error}</div> : null}
                  </td>
                  {user?.isAdmin ? (
                    <td>
                      {record.status === 'success' ? (
                        <button
                          type="button"
                          className="btn subtle"
                          disabled={rollback.isPending || running}
                          onClick={() => {
                            if (
                              window.confirm(
                                'Restore this published snapshot? Your draft content in the CMS is not affected.',
                              )
                            ) {
                              rollback.mutate(record.publishId, {
                                onSuccess: () => toast.success('Rollback started'),
                                onError: (err) => toast.error(errorMessage(err)),
                              });
                            }
                          }}
                        >
                          Roll back to this
                        </button>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        {rollback.error ? <ErrorNote error={rollback.error} /> : null}
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="stat">
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}
